import { arrayMove } from 'react-sortable-hoc'
import {
  compose,
  indexBy,
  merge,
  omit,
  partition,
  path,
  pathOr,
  prop,
  propEq,
  reject,
  sortBy,
  values,
} from 'ramda'

// move question edit state to the wrapper? This probably doesn't need to persist in the store

const initialState = {
  questions: {},
  surveyCrowds: {}, // these aren't technically crowd models - but wrappers containing crowd models
  meta: {
    redirectToQuestionId: null,
    selectedTypeId: null,
    attachmentModal: false,
    attachmentModalText: '',
    pasteMultipleModal: false,
    pasteMultipleModalText: '',
    shouldSaveQuestion: false,
    shouldSaveChoices: false,
    fetchedQuestions: false,
    fetchedRecruiter: false,
    fetchedCrowds: false,
    savingLogic: false,
    logicSaved: false,
  },
}

const resequence = arr => arr.map((el, sequence) => ({...el, sequence}))

// FIXME there's probably a much more optimal way of doing this
const resequenceObj = (oldIndex, newIndex) => compose(
  indexBy(prop('id')),
  resequence,
  arr => arrayMove(arr, oldIndex, newIndex),
  sortBy(prop('sequence')),
  values,
)

// TODO use a helper for this probably
const uid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
  const r = Math.random() * 16 | 0
  const v = c == 'x' ? r : (r & 0x3 | 0x8)
  return v.toString(16)
})

const updateQuestion = (state, props) => {
  const { id } = props
  const { questions } = state
  // TODO use needsAnyAll here
  const processAny = ![3, 6, 9].includes(props.typeId || path([id, 'typeId'], questions))

  return {
    ...state,
    questions: {
      ...questions,
      [id]: {
        logic: {
          defaultScreenIn: false,
          processAny,
          surveyQuestionLogicTypeId: 1, // screener
          maximum: null,
          minimum: null,
          logicDetails: {},
        },
        ...questions[id],
        ...props,
      },
    },
  }
}

const sortProp = (prop, state, payload) => {
  const { questionId, oldIndex, newIndex } = payload
  const question = state.questions[questionId]

  return updateQuestion(state, {
    id: questionId,
    [prop]: resequence(arrayMove(question[prop], oldIndex, newIndex)),
  })
}

const surveyReducer = (state = initialState, action) => {
  /*
   * Async Resolvers
   */
  switch (action.type) {
    case 'FETCH_RECRUITER_SUCCEEDED': {
      return {
        ...state,
        ...action.payload.json,
        meta: {
          ...state.meta,
          fetchedRecruiter: true,
        },
      }
    }
    // case 'FETCH_RECRUITER_FAILED' // TODO
    case 'FETCH_SURVEY_CROWDS_SUCCEEDED': {
      return {
        ...state,
        surveyCrowds: indexBy(prop('crowdId'), action.payload.json.surveyCrowds),
        meta: {
          ...state.meta,
          fetchedSurveyCrowds: true,
        },
      }
    }
    case 'FETCH_QUESTIONS_SUCCEEDED': {
      return {
        ...state,
        questions: {
          ...state.questions,
          ...indexBy(prop('id'), action.payload.json.questions),
        },
        meta: {
          ...state.meta,
          logicSaved: false,
          questionDeleted: false,
          fetchedQuestions: true,
        },
      }
    }
    case 'FETCH_QUESTION_TYPES_SUCCEEDED':
      return {
        ...state,
        meta: {
          ...state.meta,
          questionDeleted: false,
        },
      }
    // case 'FETCH_QUESTIONS_FAILED' // TODO
    case 'FETCH_QUESTION_SUCCEEDED': {
      return updateQuestion(state, action.payload.json)
    }
    // case 'FETCH_QUESTION_FAILED' // TODO
    case 'DUPLICATE_QUESTION_SUCCEEDED': {
      const newQuestion = action.payload.json

      return {
        ...state,
        questions: {
          ...state.questions,
          [newQuestion.id]: {
            ...newQuestion,
          },
        },
      }
    }
    // case 'DUPLICATE_QUESTION_FAILED' // TODO
    case 'DELETE_QUESTION_SUCCEEDED': {
      const deletedQuestionId = action.payload.meta.questionId

      return {
        ...state,
        questions: reject(propEq('id', deletedQuestionId))(state.questions),
        meta: {
          ...state.meta,
          questionDeleted: true,
        },
      }
    }
    // case 'DELETE_QUESTION_FAILED' // TODO
    case 'ORDER_QUESTIONS': {
      const { oldIndex, newIndex } = action.payload
      // TODO move to shared helpers and update any answer code that does a similar check
      const isScheduler = propEq('typeId', 13)

      /**
       * To be safe, we're not affecting scheduler question's sequence here.
       * It seems like API is resequencing scheduler at the end anyway but prefer to not
       * send false info from the FE only to have it ignored by the API.
       * Feel free to remove partitioning and merging if deemed excessive
       */
      const [schedulers, otherQuestions] = partition(isScheduler)(state.questions)

      return {
        ...state,
        questions: compose(
          merge(schedulers),
          resequenceObj(oldIndex, newIndex),
        )(otherQuestions),
      }
    }
    case 'ORDER_ATTACHMENTS': return sortProp('attachments', state, action.payload)
    case 'ORDER_CHOICES': return sortProp('questionChoices', state, action.payload)
    // case 'SAVE_QUESTIONS_SUCCEEDED'
    /**
     * don't really need to implement this - we only use this route for ordering questions afaik
     * if it succeeds, great, it's in the order we designated
     * feel free to do it to be extra safe
     */
    // case 'SAVE_QUESTIONS_FAILED' // TODO
    case 'ADD_QUESTION_REQUESTED': {
      return {
        ...state,
        meta: {
          ...state.meta,
          selectedTypeId: null,
        },
      }
    }
    case 'ADD_QUESTION_SUCCEEDED': {
      return {
        ...state,
        ...updateQuestion(state, action.payload.json),
      }
    }
    case 'SAVE_QUESTION_SUCCEEDED': {
      return {
        ...updateQuestion(state, action.payload.json),
        meta: {
          ...state.meta,
          shouldSaveQuestion: false,
        },
      }
    }
    case 'ADD_ATTACHMENT_SUCCEEDED': {
      const attachment = action.payload.json
      const { questionId } = attachment
      const question = state.questions[questionId]

      return {
        ...state,
        questions: {
          ...state.questions,
          [questionId]: {
            ...question,
            attachments: [
              ...question.attachments,
              attachment,
            ],
          },
        },
      }
    }
    case 'SAVE_ATTACHMENT_SUCCEEDED': {
      const attachment = action.payload.json
      const { questionId } = attachment
      const question = state.questions[questionId]

      return {
        ...state,
        questions: {
          ...state.questions,
          [questionId]: {
            ...question,
            attachments: question.attachments.map(a => a.id === attachment.id ? attachment : a),
          },
        },
      }
    }
    case 'DELETE_ATTACHMENT_SUCCEEDED': {
      const { attachmentId, questionId } = action.payload.meta
      const question = state.questions[questionId]

      return {
        ...state,
        questions: {
          ...state.questions,
          [questionId]: {
            ...question,
            attachments: reject(propEq('id', attachmentId))(question.attachments),
          },
        },
      }
    }
    case 'SAVE_CHOICE_SUCCEEDED': {
      const { uid, choiceId, questionId } = action.payload.meta
      const question = state.questions[questionId]

      return {
        ...updateQuestion(state, {
          id: questionId,
          questionChoices: question.questionChoices.map(choice => {
            if (
              (choiceId && choiceId !== choice.id) ||
              (!choiceId && uid !== choice.uid)
            ) return choice

            return {
              ...action.payload.json,
            }
          }),
        }),
        meta: {
          ...state.meta,
          shouldSaveChoices: false,
        },
      }
    }
    case 'SAVE_CHOICE_FAILED': {
      const { currentChoice, questionId } = action.payload.meta
      const { uid, id } = currentChoice
      const question = state.questions[questionId]

      return {
        ...updateQuestion(state, {
          id: questionId,
          questionChoices: question.questionChoices.map(choice => {
            if (
              (id && id !== choice.id) ||
              (!id && uid !== choice.uid)
            ) return choice

            return {
              ...choice,
              error: action.payload.error,
            }
          }),
        }),
      }
    }
    case 'FETCH_LOGIC_SUCCEEDED':
    case 'SAVE_LOGIC_SUCCEEDED': {
      const json = pathOr(action.payload.json, ['payload', 'json', 'logics', 0], action)

      return {
        ...updateQuestion(state, {
          id: json.questionId,
          logic: {
            ...json,
            logicDetails: json.logicDetails.reduce((newDetails, ld) => ({
              ...newDetails,
              [ld.questionChoiceId]: {
                ...ld,
                // TODO refactor
                label: pathOr([], ['questions', json.questionId, 'questionChoices'], state)
                  .find(qc => qc.id === ld.questionChoiceId)
                  .title,
              },
            }), {}),
          },
        }),
        meta: {
          ...state.meta,
          savingLogic: false,
          logicSaved: action.type === 'SAVE_LOGIC_SUCCEEDED',
        },
      }
    }
    case 'SAVE_LOGIC_FAILED': {
      return {
        ...state,
        meta: {
          ...state.meta,
          savingLogic: false,
        },
      }
    }
    case 'DELETE_CHOICE_SUCCEEDED': {
      const { questionId, choiceId } = action.payload.meta
      const question = state.questions[questionId]

      return updateQuestion(state, {
        id: questionId,
        questionChoices: reject(propEq('id', choiceId))(question.questionChoices),
      })
    }
    /*
     * Sync Only
     */
    case 'ADD_SURVEY_CROWD': {
      // FIXME
      // realize duplicating data in state is an antipattern but this is how we have it in core
      // is there a better way we can do this, avoiding passing a whole crowd here via component?
      const { crowd } = action.payload

      return {
        ...state,
        surveyCrowds: {
          ...state.surveyCrowds,
          [crowd.id]: {
            crowdId: crowd.id,
            answerRequest: 0,
            qualHonorarium: null,
            crowd: {
              ...crowd,
            },
          },
        },
      }
    }
    case 'REMOVE_SURVEY_CROWD': {
      const { crowdId } = action.payload

      return {
        ...state,
        surveyCrowds: reject(propEq('crowdId', crowdId))(state.surveyCrowds),
      }
    }
    case 'UPDATE_META': {
      return {
        ...state,
        meta: {
          ...state.meta,
          ...action.payload,
        },
      }
    }
    case 'UPDATE_LOGIC': {
      const { questionId } = action.payload
      const question = state.questions[questionId]

      return updateQuestion(state, {
        id: questionId,
        logic: {
          ...question.logic,
          ...omit(['questionId', 'surveyId'], action.payload),
        },
      })
    }
    case 'UPDATE_LOGIC_DETAIL': {
      const { questionChoiceId, questionId } = action.payload
      const question = state.questions[questionId]

      return updateQuestion(state, {
        id: questionId,
        logic: {
          ...question.logic,
          logicDetails: {
            ...pathOr({}, ['logic', 'logicDetails'], question),
            [questionChoiceId]: {
              ...pathOr({}, ['logic', 'logicDetails', questionChoiceId], question),
              ...omit(['questionId', 'surveyId', 'questionChoiceId'], action.payload),
            },
          },
        },
      })
    }
    case 'UPDATE_LOGIC_DETAILS': {
      const { questionId, logicDetails } = action.payload
      const question = state.questions[questionId]
      const oldDetails = pathOr({}, ['logic', 'logicDetails'], question)

      return updateQuestion(state, {
        id: questionId,
        logic: {
          ...question.logic,
          logicDetails: logicDetails.reduce((newDetails, detail) => ({
            ...newDetails,
            [detail.value]: {
              ...oldDetails[detail.value],
              questionChoiceId: detail.value,
              label: detail.label,
            },
          }), {}),
        },
      })
    }
    case 'UPDATE_QUESTION': return {
      ...updateQuestion(state, action.payload),
      meta: {
        ...state.meta,
        shouldSaveQuestion: true,
      },
    }
    case 'ADD_CHOICE': {
      const { questionId, title } = action.payload
      const question = state.questions[questionId]

      return {
        ...state,
        questions: {
          ...state.questions,
          [question.id]: {
            ...question,
            questionChoices: [
              ...question.questionChoices,
              {
                uid: uid(),
                questionId: Number(questionId),
                title: title || '',
                sequence: question.questionChoices.length,
                autoFocus: true,
              },
            ],
          },
        },
      }
    }
    case 'SAVE_CHOICE_REQUESTED':
    case 'UPDATE_CHOICE': {
      const { questionId, choiceId, uid } = action.payload
      const question = state.questions[questionId]

      return {
        ...updateQuestion(state, {
          id: questionId,
          questionChoices: question.questionChoices.map(choice => {
            if (
              (choiceId && choiceId !== choice.id) ||
              (!choiceId && uid !== choice.uid)
            ) return choice

            return {
              ...choice,
              ...action.payload,
            }
          }),
        }),
        meta: {
          ...state.meta,
          shouldSaveChoices: true,
        },
      }
    }
    // should *ONLY* be used to remove unsaved choices (sync)
    case 'REMOVE_CHOICE': {
      const { questionId, uid } = action.payload
      const question = state.questions[questionId]

      return updateQuestion(state, {
        id: questionId,
        questionChoices: reject(propEq('uid', uid))(question.questionChoices),
      })
    }
    case 'FETCH_ALL_NECESSARY_PROJECT_INFO_REQUESTED':
      return initialState
    case 'LAUNCH_SURVEYS_SUCCEEDED': {
      if (state.status) {
        const wasClosed = state.status.status === 3
        return {
          ...state,
          status: {
            ...state.status,
            status: wasClosed ? 2 : 1,
            label: wasClosed ? 'Re-Fielding' : 'Fielding',
          },
        }
      }
      return state
    }
    case 'CLOSE_SURVEYS_SUCCEEDED': {
      return {
        ...state,
        status: {
          status: 3,
          label: 'Closed',
        },
      }
    }
    default: return state
  }
}

export default surveyReducer
